import { useEffect, useState } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'

import { AuthModes } from '@/features/auth/policy'
import { Badge } from '@/shared/ui/Badge'
import { Modal } from '@/shared/ui/BaseModal'
import { Button } from '@/shared/ui/Button'
import {
  CheckboxField,
  DateField,
  DocumentField,
  Field,
  PasswordField,
  PhoneField,
  PostalCodeField,
  RadioRowField,
  SelectField,
  SwitchField,
  TextField,
  TextareaField
} from '@/shared/ui/Form'
import { FormControl } from '@/shared/ui/Form/FormControl'
import { FormGroup } from '@/shared/ui/Form/FormGroup'
import { InputGroup } from '@/shared/ui/Form/InputGroup'
import { AppIcon } from '@/shared/ui/icons'
import { Screen, PageHeader } from '@/shared/ui/Layout'
import { FilterChips } from '@/shared/ui/Layout/page/FilterChips'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'

export const Route = createFileRoute('/_event/dev/ui-primitives')({
  // Vitrine de primitivos não exige sessão: é ferramenta de desenvolvimento, e
  // o componente já devolve `null` fora de DEV. Sem isto a rota cai no modo
  // `REQUIRED` por default e o Guard manda para o login — o que também impedia
  // qualquer teste automatizado de alcançá-la.
  staticData: {
    auth: {
      mode: AuthModes.BOOTSTRAP,
      requiresDigitalProductId: false
    }
  },
  component: UiPrimitivesDevPage
})

/**
 * Vitrine dos primitivos, só em desenvolvimento.
 *
 * É onde se confere acessibilidade na prática: cada item de medição do
 * `design-system/ACCESSIBILITY.md` pode ser verificado aqui no DevTools —
 * contraste no seletor de cor, área de toque inspecionando `::after`, nome
 * acessível na aba *Accessibility*. Reunir os primitivos numa página só evita
 * ter que caçá-los pelo app.
 *
 * Ao criar primitivo novo, adicionar aqui.
 */
function UiPrimitivesDevPage() {
  if (!import.meta.env.DEV) {
    return null
  }
  return <UiPrimitivesContent />
}

function UiPrimitivesContent() {
  const methods = useForm({
    defaultValues: { email: '', senha: '', apelido: '', comErro: '' }
  })
  const camada = useForm({
    defaultValues: {
      email: '',
      senha: '',
      comErro: '',
      desabilitado: '',
      documento: '',
      telefone: '',
      cep: '',
      uf: '',
      data: '',
      observacao: '',
      aceite: false,
      notificar: true,
      porte: ''
    }
  })
  // O estado de erro precisa aparecer na vitrine: é o que se audita no
  // DevTools (aria-invalid, aria-describedby, contraste da mensagem).
  useEffect(() => {
    camada.setError('comErro', {
      type: 'manual',
      message: 'Informe um valor válido'
    })
  }, [camada])

  const [filtro, setFiltro] = useState('todas')
  const [modal, setModal] = useState<null | 'comTitulo' | 'bloqueante'>(null)

  return (
    <Screen>
      <Screen.Context>
        <PageHeader title="UI primitives (dev)" />
      </Screen.Context>
      <Screen.Content>
        <div className="gap-6 p-4 flex flex-col">
          <section>
            <h2 className="mb-3 text-base font-bold">Buttons</h2>
            <div className="gap-3 flex flex-wrap">
              <Button variant="primary">Iniciar</Button>
              <Button variant="progress">Continuar</Button>
              <Button variant="success-ghost">Revisitar</Button>
              <Button variant="primary-ghost">Detalhes</Button>
              <Button variant="danger">Cancelar</Button>
              <Button variant="primary" loading>
                Carregando
              </Button>
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold text-gray-700">
              Tamanhos — o `sm` é o CTA dos cards de jornada e missão
            </p>
            <div className="gap-3 flex flex-wrap items-center">
              <Button variant="primary" size="sm">
                Iniciar
              </Button>
              <Button variant="primary" size="md">
                Iniciar
              </Button>
              <Button variant="primary" size="lg">
                Iniciar
              </Button>
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold text-gray-700">
              Ícone + rótulo — reproduz o CTA de login social
            </p>
            <div className="gap-3 flex flex-wrap">
              <Button variant="light" className="w-full gap-2">
                <AppIcon name="google" size={20} />
                Entrar com Google
              </Button>
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold text-gray-700">
              Só ícone
            </p>
            <div className="gap-3 flex flex-wrap items-center">
              <Button
                variant="light"
                icon
                size="sm"
                aria-label="Ação pequena"
                leadingIcon={<AppIcon name="eye" />}
              />
              <Button
                variant="light"
                icon
                size="md"
                aria-label="Ação média"
                leadingIcon={<AppIcon name="eye" />}
              />
              <Button
                variant="light"
                icon
                size="lg"
                aria-label="Ação grande"
                leadingIcon={<AppIcon name="eye" />}
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">FilterChips</h2>
            <FilterChips
              aria-label="Filtrar jornadas"
              value={filtro}
              onChange={setFiltro}
              options={[
                { id: 'todas', label: 'Todas' },
                { id: 'andamento', label: 'Em andamento' },
                { id: 'novas', label: 'Novas' },
                { id: 'concluidas', label: 'Concluídas' }
              ]}
            />
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">Form</h2>
            <FormProvider {...methods}>
              <form className="flex max-w-sm flex-col">
                <FormGroup
                  vertical
                  label="E-mail"
                  name="email"
                  type="email"
                  autoComplete="email"
                  control={FormControl}
                  helpText="Usamos para enviar o comprovante"
                />
                <FormGroup
                  vertical
                  label="Apelido"
                  name="apelido"
                  control={FormControl}
                />
                <div className="mb-6">
                  <FormGroup
                    vertical
                    label="Senha"
                    name="senha"
                    control={FormControl}
                    type="password"
                    autoComplete="current-password"
                  />
                </div>
                <div className="mb-6">
                  <span className="mb-2 block text-sm font-medium text-gray-700">
                    Campo agrupado
                  </span>
                  <InputGroup>
                    <InputGroup.Text>R$</InputGroup.Text>
                    <FormControl name="comErro" aria-label="Valor" />
                  </InputGroup>
                </div>
              </form>
            </FormProvider>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">
              Form — camada canônica (alpha)
            </h2>
            <p className="mb-3 text-xs font-semibold text-gray-700">
              Campo composto por slots. Use esta camada em código novo; a acima
              segue suportada e sem depreciação.
            </p>
            <FormProvider {...camada}>
              <form className="flex max-w-sm flex-col gap-4">
                <Field name="email">
                  <Field.Label>E-mail</Field.Label>
                  <Field.Control>
                    <TextField autoComplete="email" placeholder="voce@exemplo.com" />
                  </Field.Control>
                  <Field.Description>
                    Usamos para enviar o comprovante
                  </Field.Description>
                  <Field.Error />
                </Field>

                <Field name="senha">
                  <Field.Label>Senha</Field.Label>
                  <Field.Control>
                    <PasswordField autoComplete="current-password" />
                  </Field.Control>
                </Field>

                <Field name="comErro">
                  <Field.Label>Campo com erro</Field.Label>
                  <Field.Control>
                    <TextField />
                  </Field.Control>
                  <Field.Error />
                </Field>

                <Field name="desabilitado">
                  <Field.Label>Desabilitado</Field.Label>
                  <Field.Control>
                    <TextField disabled placeholder="Sem edição" />
                  </Field.Control>
                </Field>

                <Field name="documento">
                  <Field.Label>Documento</Field.Label>
                  <Field.Control>
                    <DocumentField />
                  </Field.Control>
                  <Field.Description>Alterna entre CPF e CNPJ</Field.Description>
                </Field>

                <Field name="telefone">
                  <Field.Label>Telefone</Field.Label>
                  <Field.Control>
                    <PhoneField />
                  </Field.Control>
                </Field>

                <Field name="cep">
                  <Field.Label>CEP</Field.Label>
                  <Field.Control>
                    <PostalCodeField />
                  </Field.Control>
                </Field>

                <Field name="uf">
                  <Field.Label>Estado</Field.Label>
                  <Field.Control>
                    <SelectField
                      options={[
                        { value: 'SP', label: 'São Paulo' },
                        { value: 'RJ', label: 'Rio de Janeiro' },
                        { value: 'MG', label: 'Minas Gerais' }
                      ]}
                    />
                  </Field.Control>
                </Field>

                <Field name="data">
                  <Field.Label>Data</Field.Label>
                  <Field.Control>
                    <DateField />
                  </Field.Control>
                </Field>

                <Field name="observacao">
                  <Field.Label>Observação</Field.Label>
                  <Field.Control>
                    <TextareaField placeholder="Conte o que achou" />
                  </Field.Control>
                </Field>

                <CheckboxField
                  name="aceite"
                  label="Li e aceito os termos"
                  description="O alvo de toque é o rótulo inteiro, não a caixa"
                />

                <SwitchField
                  name="notificar"
                  label="Receber avisos"
                  description="Interruptor à direita, no padrão de ajuste"
                />

                <RadioRowField
                  name="porte"
                  legend="Porte do evento"
                  options={[
                    { value: 'p', label: 'Até 50 pessoas' },
                    { value: 'm', label: 'De 51 a 200 pessoas' },
                    { value: 'g', label: 'Mais de 200 pessoas' }
                  ]}
                />
              </form>
            </FormProvider>

            <p className="mb-2 mt-6 text-xs font-semibold text-gray-700">
              Campo e botão de mesmo tamanho fecham a mesma altura — é o que os
              tokens `--control-h-*` garantem. Um `Button` sem `variant` cai no
              caminho legado e não recebe altura.
            </p>
            <FormProvider {...camada}>
              <div className="flex max-w-sm flex-col gap-3">
                {(['sm', 'md', 'lg'] as const).map(size => (
                  <div key={size} className="flex items-center gap-2">
                    <Field name="email" className="flex-1">
                      <Field.Control>
                        <TextField size={size} placeholder={size} />
                      </Field.Control>
                    </Field>
                    <Button variant="primary" size={size}>
                      Buscar
                    </Button>
                  </div>
                ))}
              </div>
            </FormProvider>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">Dialog</h2>
            <div className="gap-3 flex flex-wrap">
              <Button variant="primary" onClick={() => setModal('comTitulo')}>
                Abrir com título
              </Button>
              <Button variant="light" onClick={() => setModal('bloqueante')}>
                Abrir bloqueante
              </Button>
            </div>
            <Modal
              show={modal === 'comTitulo'}
              onHide={() => setModal(null)}
              title="Diálogo de exemplo"
            >
              <Modal.Body>
                <p className="text-gray-700">
                  Conteúdo curto, que cabe na viewport.
                </p>
                <div style={{ height: 900 }} aria-hidden>
                  {/* força conteúdo maior que a viewport para o teste de rolagem */}
                </div>
                <Button variant="primary">Ação ao fim do conteúdo</Button>
              </Modal.Body>
            </Modal>
            <Modal show={modal === 'bloqueante'} title="Diálogo sem saída">
              <Modal.Body>
                <p className="text-gray-700">
                  Sem `onHide` — não deve renderizar botão de fechar.
                </p>
                <Button variant="light" onClick={() => setModal(null)}>
                  Fechar pelo conteúdo
                </Button>
              </Modal.Body>
            </Modal>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">Badges</h2>
            <div className="gap-2 flex flex-wrap">
              <Badge variant="available">Disponível</Badge>
              <Badge variant="new">Nova</Badge>
              <Badge variant="progress">Em andamento</Badge>
              <Badge variant="success">Concluída</Badge>
              <Badge variant="locked">Bloqueada</Badge>
              <Badge variant="expired">Encerrada</Badge>
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold text-gray-700">Light</p>
            <div className="gap-2 flex flex-wrap">
              <Badge variant="available" tone="light">
                Disponível
              </Badge>
              <Badge variant="progress" tone="light">
                Em andamento
              </Badge>
              <Badge variant="success" tone="light">
                Concluída
              </Badge>
              <Badge variant="locked" tone="light">
                Bloqueada
              </Badge>
              <Badge variant="expired" tone="light">
                Encerrada
              </Badge>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">Progress</h2>
            <div className="gap-4 flex flex-col" style={{ maxWidth: 320 }}>
              <ProgressBar value={38} variant="primary" />
              <ProgressBar value={45} variant="progress" />
              <ProgressBar value={100} variant="success" />
              <ProgressBar value={20} variant="locked" />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-base font-bold">Rewards</h2>
            <RewardBadge.Group>
              <RewardBadge type="xp" value={150} />
              <RewardBadge type="coins" value={50} />
              <RewardBadge type="stamp" value={1} />
            </RewardBadge.Group>
          </section>
        </div>
      </Screen.Content>
    </Screen>
  )
}
